import React, { useState } from "react";
import { FaMapPin, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import apiClient from '../utils/apiClient';

const Contact = () => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState(""); // "success" or "error"

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMessage("");

    try {
      const response = await apiClient.post("/send-email", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        message: form.message,
      });

      if (response.data && response.data.success) {
        setStatusType("success");
        setStatusMessage("Thank you! Your message has been sent successfully.");
        setForm({ firstName: "", lastName: "", email: "", message: "" });
      } else {
        setStatusType("error");
        setStatusMessage(response.data?.message || "Failed to send message. Please try again.");
      }
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error.response?.data?.message || "An error occurred. Please try again later.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-[calc(100vh-70px)] p-8 md:p-4 lg:p-8 flex flex-col items-center">
      <div className="flex flex-col lg:flex-row gap-8 md:gap-6 lg:gap-12 max-w-6xl w-full">

        {/* Left Section - Form */}
        <div className="flex-1 min-w-0">
          <h2 className="text-4xl md:text-3xl lg:text-4xl font-bold mb-4">Contact Us</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            We are deeply committed to delivering unparalleled service and unwavering support to ensure your experience exceeds expectations.
          </p>

          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="premium-label">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="John"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="premium-input"
                />
              </div>
              <div className="flex-1">
                <label className="premium-label">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="premium-input"
                />
              </div>
            </div>

            <div>
              <label className="premium-label">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                name="email"
                placeholder="john.doe@example.com"
                value={form.email}
                onChange={handleChange}
                required
                disabled={loading}
                className="premium-input"
              />
            </div>

            <div>
              <label className="premium-label">Description <span className="text-red-500">*</span></label>
              <textarea
                name="message"
                placeholder="How can we help you today?"
                rows="5"
                value={form.message}
                onChange={handleChange}
                required
                disabled={loading}
                className="premium-input resize-none"
              ></textarea>
            </div>

            {statusMessage && (
              <div className={`p-3 rounded-lg text-sm ${statusType === "success"
                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                  : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                }`}>
                {statusMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:grayscale"
            >
              {loading ? "Sending Message..." : "Send Message"}
            </button>
          </form>
        </div>

        {/* Right Section - Info Cards */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {/* Address Card - Blue */}
          <div className="bg-blue-600 text-white rounded-xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <FaMapPin className="text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold mb-2">Address</h3>
                <p className="text-sm leading-relaxed">Kestopur Chandiberia Near Power House, P.S. New Town, North 24 Parganas, West Bengal – 700102</p>
              </div>
            </div>
          </div>

          {/* Contact Card */}
          <div className="bg-blue-100 dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <FaPhone className="text-blue-600 dark:text-blue-400 text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Contact</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Talk to us and see how we can work<br />+91 9477879491</p>
              </div>
            </div>
          </div>

          {/* Email Card */}
          <div className="bg-blue-100 dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <FaEnvelope className="text-blue-600 dark:text-blue-400 text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Email</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">We're usually replying within 24 hours<br />contact@rtobharat.com</p>
              </div>
            </div>
          </div>

          {/* Working Hours Card */}
          <div className="bg-blue-100 dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <FaClock className="text-blue-600 dark:text-blue-400 text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Working Hours</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Mon To Sat - 10 am To 7 pm<br />Sunday - 11am To 5 pm</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;